import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { Color } from '@tiptap/extension-color';
import { Emoji, emojiToShortcode, shortcodeToEmoji } from '@tiptap/extension-emoji';
import { Highlight } from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextStyle } from '@tiptap/extension-text-style';
import { Typography } from '@tiptap/extension-typography';
import { mergeAttributes, ReactNodeViewRenderer } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { common, createLowlight } from 'lowlight';
import { CodeBlockNodeView } from './code-block-node-view';
import { buildEmojiSuggestion } from './emoji/suggestion';
import { buildTableExtensions } from './table/table-kit';

const lowlight = createLowlight(common);

const CodeBlockWithNodeView = CodeBlockLowlight.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockNodeView);
  },
});

// The backend strips `data-*` attributes (and `draggable`, `loading`) but preserves `class`.
// We feed `class="emoji"` via `HTMLAttributes` so the surviving class can re-identify the span
// on the next parse. Default renderHTML already merges HTMLAttributes onto the span. No override needed.
const EmojiWithClassFallback = Emoji.extend({
  parseHTML() {
    return [
      // Default canonical rule (data-type + data-name). Inherited so we don't drift
      // from upstream if it changes.
      ...(this.parent?.() ?? []),
      // Fallback after the backend strip: `data-*` is gone but `class="emoji"` survives.
      // Recover the canonical `name` from whatever the span still contains. Returning
      // `false` here lets the parser fall through, and the Image extension's `img[src]`
      // rule then wraps the inner fallback img in a resize NodeView, so try every
      // signal we have before giving up.
      //   1. Unicode text content (text-rendered emojis)
      //   2. The img's `alt="<name> emoji"` (default fallback-image renderer)
      // `emojiToShortcode` returns shortcodes[0] (e.g. "grinning_face_with_closed_eyes"
      // for :smile:); a second lookup converts that back to the canonical `name`.
      {
        tag: 'span.emoji',
        getAttrs: (element) => {
          if (typeof element === 'string') {
            return false;
          }
          const text = element.textContent?.trim() ?? '';
          if (text) {
            const shortcode = emojiToShortcode(text, this.options.emojis);
            if (shortcode) {
              const item = shortcodeToEmoji(shortcode, this.options.emojis);
              if (item?.name) {
                return { name: item.name };
              }
            }
          }
          const img = element.querySelector('img');
          if (img) {
            const alt = (img.getAttribute('alt') ?? '').replace(/\s*emoji$/i, '').trim();
            const item = this.options.emojis.find((e) => e.name === alt);
            if (item) {
              return { name: item.name };
            }
          }
          return false;
        },
      },
    ];
  },
});

// <mark> is stripped on record save. Render as <span style="background-color: ..."> instead.
// It's the same shape TextStyle/Color use for text colour, which survives the round-trip.
const HighlightAsSpan = Highlight.extend({
  parseHTML() {
    return [
      {
        tag: 'span',
        getAttrs: (node) => {
          if (typeof node === 'string') {
            return false;
          }
          // Only treat <span> as a highlight when it carries a background-color.
          // Avoids matching TextStyle's `<span style="color: ...">`.
          return node.style.backgroundColor ? {} : false;
        },
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
});

export interface BuildExtensionsOptions {
  placeholder?: string;
  /**
   * Whether the resulting extension list is for an editable editor (true) or the viewer
   * (false). Defaults to true. Affects `Link.openOnClick`: in the editor clicking a link
   * positions the cursor; in the viewer clicks should navigate.
   */
  editable?: boolean;
}

export const buildBaseExtensions = ({ placeholder, editable = true }: BuildExtensionsOptions = {}) => [
  // Disable the StarterKit extensions we replace with custom configs below.
  StarterKit.configure({
    codeBlock: false,
    link: false,
    dropcursor: {
      color: 'var(--color-primary-3)',
      width: 2,
    },
  }),
  Link.configure({
    openOnClick: !editable,
    defaultProtocol: 'https',
    autolink: true,
    linkOnPaste: true,
    HTMLAttributes: {
      rel: 'noopener noreferrer nofollow',
      target: '_blank',
      spellcheck: 'false',
    },
  }),
  Image.configure({
    inline: false,
    allowBase64: true,
    resize: {
      enabled: true,
      directions: ['right'],
      minWidth: 50,
      minHeight: 50,
      alwaysPreserveAspectRatio: true,
    },
  }),
  Subscript,
  Superscript,
  // TextStyle is the carrier mark for Color (and any other inline style attributes).
  TextStyle,
  Color,
  HighlightAsSpan.configure({ multicolor: true }),
  EmojiWithClassFallback.configure({
    enableEmoticons: true,
    HTMLAttributes: { class: 'emoji' },
    suggestion: buildEmojiSuggestion(),
  }),
  Typography,
  CodeBlockWithNodeView.configure({ lowlight }),
  Placeholder.configure({
    placeholder: placeholder ?? '',
    showOnlyWhenEditable: true,
    showOnlyCurrent: false,
  }),
  ...buildTableExtensions(),
];
